import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { apiRequest, authService } from '../utils/auth';
import { 
  Plus, Search, Edit, Trash2, Copy, Eye, Loader2, 
  Calculator, ChevronLeft, ChevronRight, FileText
} from 'lucide-react';

export default function QuotationCostingList() {
  const navigate = useNavigate();
  const user = authService.getUser();
  const isAdmin = user?.role === 'admin';

  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteId, setDeleteId] = useState(null);
  const [duplicating, setDuplicating] = useState(null);

  useEffect(() => {
    fetchQuotations();
  }, [page, search]);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.append('search', search);
      
      const data = await apiRequest(`/api/quotation-costing?${params}`);
      setQuotations(data.quotations || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch quotations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await apiRequest(`/api/quotation-costing/${deleteId}`, { method: 'DELETE' });
      toast.success('Quotation deleted successfully');
      fetchQuotations();
    } catch (error) {
      toast.error(error.message || 'Failed to delete quotation');
    } finally {
      setDeleteId(null);
    }
  };

  const handleDuplicate = async (id) => {
    setDuplicating(id);
    try {
      const result = await apiRequest(`/api/quotation-costing/${id}/duplicate`, { method: 'POST' });
      toast.success('Quotation duplicated successfully');
      navigate(`/quotation-costing/${result.id}`);
    } catch (error) {
      toast.error(error.message || 'Failed to duplicate quotation');
    } finally {
      setDuplicating(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="quotation-costing-list-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2540]">Quotation Costing</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage manufacturing cost calculations and quotations
            </p>
          </div>
          <Button 
            onClick={() => navigate('/quotation-costing')} 
            className="bg-[#0A2540] hover:bg-[#0A2540]/90"
            data-testid="create-quotation-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Quotation
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-white border-blue-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calculator className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Quotations</p>
                <p className="text-2xl font-bold text-[#0A2540]">{total}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-green-50 to-white border-green-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">This Month</p>
                <p className="text-2xl font-bold text-green-600">
                  {quotations.filter(q => {
                    const date = new Date(q.created_at);
                    const now = new Date();
                    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-purple-50 to-white border-purple-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Eye className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Showing</p>
                <p className="text-2xl font-bold text-purple-600">{quotations.length}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Search */}
        <Card className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by customer, part number, or part name..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" onClick={fetchQuotations}>
              <Search className="w-4 h-4" />
            </Button>
          </div>
        </Card>

        {/* Table */}
        <Card>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : quotations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Calculator className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">No quotations found</p>
              <p className="text-sm">Create your first quotation to get started</p>
              <Button 
                onClick={() => navigate('/quotation-costing')} 
                className="mt-4 bg-[#0A2540]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Quotation
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Customer</TableHead>
                    <TableHead className="font-semibold">Part Number</TableHead>
                    <TableHead className="font-semibold">Part Name</TableHead>
                    <TableHead className="font-semibold">Material</TableHead>
                    <TableHead className="font-semibold text-right">Mfg Cost</TableHead>
                    <TableHead className="font-semibold text-right">Final Value</TableHead>
                    <TableHead className="font-semibold text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotations.map((q) => (
                    <TableRow 
                      key={q.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/quotation-costing/${q.id}`)}
                      data-testid={`quotation-row-${q.id}`}
                    >
                      <TableCell className="text-sm">
                        {formatDate(q.header?.date || q.created_at)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {q.header?.customer_name || '-'}
                      </TableCell>
                      <TableCell className="text-blue-600 font-mono">
                        {q.header?.part_number || '-'}
                      </TableCell>
                      <TableCell>{q.header?.part_name || '-'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          q.raw_material?.material_type === 'Aluminium' ? 'bg-blue-100 text-blue-700' :
                          q.raw_material?.material_type === 'Copper' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {q.raw_material?.material_type || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(q.summary?.total_manufacturing_cost)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-green-600 font-mono">
                        {formatCurrency(q.summary?.final_quotation_value)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/quotation-costing/${q.id}`)}
                            title="Edit"
                            data-testid={`edit-btn-${q.id}`}
                          >
                            <Edit className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDuplicate(q.id)}
                            disabled={duplicating === q.id}
                            title="Duplicate"
                            data-testid={`duplicate-btn-${q.id}`}
                          >
                            {duplicating === q.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Copy className="w-4 h-4 text-purple-600" />
                            )}
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteId(q.id)}
                              title="Delete"
                              data-testid={`delete-btn-${q.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages} ({total} total)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Quotation?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the quotation costing.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
